import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Typography } from "@mui/material";
import { Select } from "@outpave/ds";
import ApiKeyRole from "enums/ApiKeyRole";
import type { ApiKeyPermissionCatalogue } from "hooks/useApiKeys";
import { Inline, Stack } from "components/layout/reusableLayouts";
import {
  CUSTOM_ACCESS,
  apiKeyPresetCaptionKey,
  apiKeyPresetLabelKey,
  canChoosePreset,
  countSensitive,
  getAccessChoice,
  getPresetPermissions,
} from "utils/apiKeys/apiKeyPermissions";
import ApiKeyAdvancedPermissionsDrawer from "./ApiKeyAdvancedPermissionsDrawer";

interface ApiKeyAccessFieldProps {
  catalogue: ApiKeyPermissionCatalogue;
  selected: string[];
  onSelectedChange: (next: string[]) => void;
  // The role the key is saved with, which the selection decides.
  role: ApiKeyRole;
  versionHelper: string;
  disabled?: boolean;
}

const ApiKeyAccessField: React.FC<ApiKeyAccessFieldProps> = ({
  catalogue,
  selected,
  onSelectedChange,
  role,
  versionHelper,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const choice = getAccessChoice(
    selected,
    catalogue.presets,
    catalogue.permissions,
  );
  const sensitive = countSensitive(selected, catalogue.permissions);
  const access = t(
    role === ApiKeyRole.ReadOnly
      ? "ApiKeyAccessReadOnly"
      : "ApiKeyAccessReadWrite",
  );

  const options = [
    ...catalogue.presets.map((preset) => {
      const available = canChoosePreset(preset, catalogue.permissions);
      return {
        id: preset.id,
        label: t(apiKeyPresetLabelKey(preset.id), { defaultValue: preset.id }),
        caption: available
          ? t(apiKeyPresetCaptionKey(preset.id), { defaultValue: "" })
          : t("ApiKeyPresetOwnerOnly"),
        disabled: !available,
      };
    }),
    {
      id: CUSTOM_ACCESS,
      label: t("Custom"),
      caption: t("ApiKeyPresetCaption_custom"),
      disabled: true,
    },
  ];

  return (
    <Stack gap="var(--space-2)">
      <Select
        label={t("ApiKeyAccess")}
        value={choice}
        onChange={(value) => {
          const preset = catalogue.presets.find((p) => p.id === value);
          if (preset) {
            onSelectedChange(
              getPresetPermissions(preset, catalogue.permissions),
            );
          }
        }}
        clearable={false}
        fullWidth
        disabled={disabled}
        options={options}
      />
      <Inline gap="var(--space-3)" justify="between" wrap={false}>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {sensitive > 0
            ? t("ApiKeyAccessSummarySensitive", {
                access,
                count: selected.length,
                sensitive,
              })
            : t("ApiKeyAccessSummary", { access, count: selected.length })}
        </Typography>
        <Button
          variant="primary-soft"
          onClick={() => setAdvancedOpen(true)}
          disabled={disabled}
        >
          {t("Advanced")}
        </Button>
      </Inline>
      <ApiKeyAdvancedPermissionsDrawer
        open={advancedOpen}
        catalogue={catalogue}
        selected={selected}
        versionHelper={versionHelper}
        onApply={onSelectedChange}
        onClose={() => setAdvancedOpen(false)}
      />
    </Stack>
  );
};

export default ApiKeyAccessField;
